import React, { useState, useCallback, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";

const containerStyle = {
  width: "100%",
  height: window.innerWidth < 768 ? "185px" : "calc(100vh - 650px)",
  minHeight: "170px",
  maxHeight: "600px",
  borderRadius: "0.375rem",
  overflow: "hidden",
  boxShadow:
    "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
};

const center = {
  lat: 43.65,
  lng: -79.38,
};

interface LocationDetails {
  lat: number;
  lng: number;
  address: string;
  cityName: string;
  state: string;
  zipCode: string;
}

const libraries: any = ["places"];

interface MyGoogleMapProps {
  onLocationSelect: (details: LocationDetails) => void;
  initialLocation?: { lat: number; lng: number };
}

// Add styles for the autocomplete dropdown
const autocompleteStyles = {
  container: {
    position: "relative",
    width: "100%",
  } as React.CSSProperties,
  suggestionsContainer: {
    maxHeight: "200px",
    overflowY: "auto",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
    borderRadius: "0.375rem",
    border: "1px solid #e5e7eb",
    backgroundColor: "white",
    zIndex: 1000,
    position: "absolute",
    width: "100%",
  } as React.CSSProperties,
};

const MyGoogleMap: React.FC<MyGoogleMapProps> = ({
  onLocationSelect,
  initialLocation,
}) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyA70RDovtCyeZisoqgzrpB4_hQ4_cQTOes",
    libraries: libraries,
    region: "ca",
    language: "en",
  });

  const [markerPosition, setMarkerPosition] = useState(
    initialLocation || center
  );
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const handleScroll = (event: Event) => {
      // Check if target exists and is an Element before checking classList
      if (
        event.target instanceof Element &&
        event.target.classList.contains("pac-container")
      ) {
        return;
      }

      // Only close the pac-container if it's visible
      const pacContainers = document.getElementsByClassName("pac-container");
      if (pacContainers.length > 0) {
        for (let i = 0; i < pacContainers.length; i++) {
          const container = pacContainers[i] as HTMLElement;
          if (container.style.display !== "none") {
            container.style.display = "none";
          }
        }
      }
    };

    // Use passive event listener for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarkerPosition({ lat, lng });
      geocodeLatLng(lat, lng);
    }
  }, []);

  const geocodeLatLng = (lat: number, lng: number) => {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results && results[0]) {
        const addressComponents = results[0].address_components;
        const address = results[0].formatted_address;
        const cityName =
          addressComponents.find((c) => c.types.includes("locality"))
            ?.long_name || "";
        const state =
          addressComponents.find((c) =>
            c.types.includes("administrative_area_level_1")
          )?.long_name || "";
        const zipCode =
          addressComponents.find((c) => c.types.includes("postal_code"))
            ?.long_name || "";

        onLocationSelect({
          lat,
          lng,
          address,
          cityName,
          state,
          zipCode,
        });
      } else {
        console.error(
          "Geocode was not successful for the following reason:",
          status
        );
      }
    });
  };

  const onMarkerDragEnd = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMarkerPosition({ lat, lng });
      geocodeLatLng(lat, lng);
    }
  };

  const onPlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();

      if (place && place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();

        if (lat && lng) {
          setMarkerPosition({ lat, lng });
          geocodeLatLng(lat, lng);
        }
      } else {
        console.error("Selected place does not have geometry data.");
      }
    }
  };

  if (!isLoaded)
    return (
      <div className="w-full h-[400px] rounded bg-gray-300 animate-pulse"></div>
    );

  return (
    <div className="rounded w-full ">
      <div className="mb-4" style={autocompleteStyles.container}>
        <Autocomplete
          onLoad={(autocomplete) => {
            autocompleteRef.current = autocomplete;
          }}
          onPlaceChanged={onPlaceChanged}
          options={{
            types: ["establishment"],
            componentRestrictions: { country: "ca" },
          }}
        >
          <input
            ref={inputRef}
            type="text"
            placeholder="Select Address"
            className="block w-full px-2 xxl:py-3 py-2 border border-gray-300 focus:border-customGreenColor focus:outline-customGreenColor rounded-lg"
          />
        </Autocomplete>
        <style>
          {`
            .pac-container {
              background-color: white;
              border: 1px solid #e5e7eb;
              border-radius: 0.375rem;
              box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
              margin-top: 4px;
              z-index: 1000;
              position: absolute !important;
            }
            .pac-item {
              padding: 8px 12px;
              cursor: pointer;
            }
            .pac-item:hover {
              background-color: #f3f4f6;
            }
            .pac-item-selected {
              background-color: #e5e7eb;
            }
          `}
        </style>
      </div>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPosition}
        zoom={15}
        onClick={onMapClick}
        onLoad={(map) => {
          mapRef.current = map;
        }}
      >
        {markerPosition && (
          <Marker
            position={markerPosition}
            draggable
            onDragEnd={onMarkerDragEnd}
          />
        )}
      </GoogleMap>
    </div>
  );
};

export default React.memo(MyGoogleMap);
